import type { TreeData } from '../../../../shared/components/tree/useTreeApi';
import type { AllSelectionListsWithSkillTaxonomyKey } from '../types/filters';

type NestedTaxonomyTreeNode = TreeData<{
  levelId: AllSelectionListsWithSkillTaxonomyKey;
}>;

const isValidTaxonomyNodeName = (name: string) => {
  const normalizedName = name.trim().toLowerCase();
  return normalizedName !== '' && normalizedName !== 'empty';
};

export const toNestedTaxonomyTree = (
  nodes: TreeData[],
  selectionListIds: readonly AllSelectionListsWithSkillTaxonomyKey[],
  parentId?: string,
  sourceDepth = 0,
  includeNodeMetadata = true,
  filterInvalidNodeNames = false,
  visibleSourceDepths?: number[],
  visibleDepth = 0
): NestedTaxonomyTreeNode[] => {
  const result: NestedTaxonomyTreeNode[] = [];

  for (const node of nodes) {
    if (filterInvalidNodeNames && !isValidTaxonomyNodeName(node.name)) {
      continue;
    }

    const isVisibleDepth =
      visibleSourceDepths === undefined ||
      visibleSourceDepths.includes(sourceDepth);

    const id = isVisibleDepth && parentId ? `${parentId}_${node.id}` : node.id;
    const childParentId = isVisibleDepth ? id : parentId;

    let children: NestedTaxonomyTreeNode[] | undefined;
    if (node.children && node.children.length > 0) {
      children = toNestedTaxonomyTree(
        node.children,
        selectionListIds,
        childParentId,
        sourceDepth + 1,
        includeNodeMetadata,
        filterInvalidNodeNames,
        visibleSourceDepths,
        isVisibleDepth ? visibleDepth + 1 : visibleDepth
      );
      if (children.length === 0) {
        children = undefined;
      }
    }

    if (!isVisibleDepth) {
      if (children) {
        for (const child of children) {
          result.push(child);
        }
      }
      continue;
    }

    const levelId =
      selectionListIds[visibleDepth] ??
      selectionListIds[selectionListIds.length - 1];
    if (levelId === undefined) {
      continue;
    }

    if (includeNodeMetadata) {
      const { children: _ignoredChildren, ...nodeWithoutChildren } = node;
      result.push({
        ...nodeWithoutChildren,
        id,
        levelId,
        ...(children ? { children } : {}),
      });
    } else {
      result.push({
        id,
        name: node.name,
        levelId,
        ...(node.depth !== undefined ? { depth: node.depth } : {}),
        ...(children ? { children } : {}),
      });
    }
  }

  return result;
};
